import React from 'react';
import {
  Icon, Spinner, Alert, Intent
} from '@blueprintjs/core';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { formatTime } from '../../helpers/time';
import moment from 'moment-timezone';
import Toast from 'helpers/toaster';

const initialsOf = (name = '') =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

@inject('store','viewStore', 'jobStore', 'activityStore') @observer
class NotesTable extends React.Component {

  @observable isDeleteDialogVisible = false;
  @observable note;
  @observable draft = '';
  @observable sending = false;
  @observable selectedId = null; // the note whose actions are showing (click / tap)
  @observable editingId = null; // the note being edited in place
  @observable editDraft = '';
  @observable savingEdit = false;
  listRef = React.createRef();
  inputRef = React.createRef();
  lastCount = 0;

  componentDidMount() {
    let { job } = this.props;
    this.props.jobStore.loadNotes(job)  
  }

  componentDidUpdate(prevProps) {
    const previousId = prevProps.job?.attributes?.id;
    const nextId = this.props.job?.attributes?.id;
    if (nextId && nextId !== previousId) {
      this.props.jobStore.loadNotes(this.props.job);
    }
  }

  onSucessNotes() {
    let {
      jobStore,
      jobStore: {
        job: {
          object
        }
      },
      activityStore
    } = this.props;


    jobStore.refreshJob({ uuid: object.attributes.id })
      .then((result) => {
        if (result.applied) {
          activityStore.loadActivityLogs(object.attributes.id);
        }
      })
  }

  startDeleteNote(note) {
    this.note = note;
    this.isDeleteDialogVisible = true;
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    const { job } = this.props;
    this.props.jobStore.deleteNote(this.note, job)
      .then(this.onSuccessfulDeleteNote.bind(this))
      .catch(this.onFailDeleteNote.bind(this))
  }

  onSuccessfulDeleteNote(response) {
    let {
      jobStore,
      jobStore: {
        job: {
          object
        }
      },
      activityStore
    } = this.props;

    Toast.show({ message: 'Note deleted.', intent: Intent.SUCCESS })
    this.note = null;
    this.isDeleteDialogVisible = false;

    jobStore.refreshJob({ uuid: object.attributes.id })
      .then((result) => {
        if (result.applied) {
          activityStore.loadActivityLogs(object.attributes.id);
        }
      })
  }

  onFailDeleteNote(error) {
    this.isDeleteDialogVisible = false;
    if(error.response && error.response.data && error.response.data.errors) {
      Toast.show({ icon: 'warning-sign', message: error.response.data.errors, intent: Intent.DANGER })
    } else {
      Toast.show({ message: 'Couldn’t delete the note. Try again.', intent: Intent.DANGER })
    }
  }

  // Post what's in the box as a note, then refresh the job (counts, activity).
  sendNote = () => {
    const body = this.draft.trim();
    if (!body || this.sending) return;
    const { jobStore } = this.props;
    this.sending = true;
    jobStore.createNotes(jobStore.job.object, body)
      .then(() => {
        this.draft = '';
        this.sending = false;
        this.focusComposer();
        this.onSucessNotes();
      })
      .catch(() => {
        this.sending = false;
        this.focusComposer();
        Toast.show({ message: 'Couldn’t add the note. Try again.', intent: Intent.DANGER });
      });
  };

  // Back in the box, ready for the next note (once it's enabled again).
  focusComposer() {
    setTimeout(() => this.inputRef.current && this.inputRef.current.focus(), 0);
  }

  toggleSelected(note) {
    if (this.editingId) return;
    this.selectedId = this.selectedId === note.id ? null : note.id;
  }

  startEdit(note) {
    this.editingId = note.id;
    this.editDraft = note.attributes.body || '';
    this.selectedId = null;
  }

  cancelEdit = () => {
    this.editingId = null;
    this.editDraft = '';
  };

  saveEdit(note) {
    const body = this.editDraft.trim();
    if (!body || this.savingEdit) return;
    if (body === (note.attributes.body || '').trim()) {
      this.cancelEdit();
      return;
    }
    const { jobStore } = this.props;
    this.savingEdit = true;
    jobStore.updateNotes(jobStore.job.object, body, note.attributes)
      .then(() => {
        this.savingEdit = false;
        this.cancelEdit();
        this.onSucessNotes();
      })
      .catch(() => {
        this.savingEdit = false;
        Toast.show({ message: 'Couldn’t save the note. Try again.', intent: Intent.DANGER });
      });
  }

  onEditKeyDown(event, note) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      this.saveEdit(note);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelEdit();
    }
  }

  onComposerKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      this.sendNote();
    }
  };

  componentDidUpdate() {
    // Keep the newest note in view, like a chat.
    const count = (this.props.jobStore.notes.objects || []).length;
    if (count !== this.lastCount && this.listRef.current) {
      this.listRef.current.scrollTop = this.listRef.current.scrollHeight;
    }
    this.lastCount = count;
  }

  dayLabel(date) {
    const day = moment(date);
    if (day.isSame(moment(), 'day')) return 'Today';
    if (day.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
    return day.format(day.isSame(moment(), 'year') ? 'ddd, MMM D' : 'ddd, MMM D, YYYY');
  }

  render() {
    const { jobStore: { notes: { isLoading, objects } }, viewStore: { currentUser } } = this.props;
    const notes = (objects || []).slice().sort((x, y) => new Date(x.attributes.created_at) - new Date(y.attributes.created_at));
    let lastDay = null;

    return (
      <div className="jp-chat">
        <div className="jp-chat__head">
          <h2 className="jp-details__title">Notes</h2>
          {notes.length > 0 && <span className="jp-chat__count">{notes.length}</span>}
        </div>

        <div className="jp-chat__list" ref={this.listRef} aria-live="polite">
          {isLoading && notes.length === 0 && <div className="jp-chat__status"><Spinner size={16} /></div>}
          {!isLoading && notes.length === 0 && (
            <p className="jp-chat__empty">No notes yet. Write one below for the team, like a gate code or what to bring.</p>
          )}
          {notes.map((note, index) => {
            const a = note.attributes;
            // Notes from the same person a few minutes apart read as one run:
            // name on the first, time (and actions) on the last.
            const sameRun = (other) => other
              && other.attributes.user_id === a.user_id
              && Math.abs(moment(other.attributes.created_at).diff(moment(a.created_at), 'minutes')) <= 5
              && moment(other.attributes.created_at).isSame(moment(a.created_at), 'day');
            const continues = sameRun(notes[index - 1]);
            const continued = sameRun(notes[index + 1]);
            const selected = this.selectedId === note.id;
            const editing = this.editingId === note.id;
            const mine = currentUser && a.user_id === currentUser.id;
            const day = moment(a.created_at).format('YYYY-MM-DD');
            const divider = day !== lastDay ? this.dayLabel(a.created_at) : null;
            lastDay = day;
            return (
              <React.Fragment key={note.id}>
                {divider && <div className="jp-chat__day"><span>{divider}</span></div>}
                <div className={`jp-msg${mine ? ' is-mine' : ''}${continues ? ' is-continued' : ''}${continued && !editing ? ' has-more' : ''}${selected ? ' is-selected' : ''}${editing ? ' is-editing' : ''}`}>
                  {!mine && (
                    continued
                      ? <span className="jp-msg__avatar is-spacer" aria-hidden="true" />
                      : <span className="jp-msg__avatar" aria-hidden="true">{initialsOf(a.user_name)}</span>
                  )}
                  {mine && !editing && (
                    <span className="jp-msg__actions">
                      <button type="button" onClick={() => this.startEdit(note)} aria-label="Edit note" title="Edit">
                        <Icon icon="edit" iconSize={12} />
                      </button>
                      <button type="button" onClick={() => { this.selectedId = null; this.startDeleteNote(note); }} aria-label="Delete note" title="Delete">
                        <Icon icon="trash" iconSize={12} />
                      </button>
                    </span>
                  )}
                  <div className="jp-msg__body">
                    {!mine && !continues && <span className="jp-msg__who">{a.user_name}</span>}
                    {editing ? (
                      <div className="jp-msg__edit">
                        <textarea
                          className="jp-chat__input"
                          aria-label="Edit note"
                          value={this.editDraft}
                          disabled={this.savingEdit}
                          autoFocus
                          onFocus={(event) => { const v = event.target.value; event.target.setSelectionRange(v.length, v.length); }}
                          onChange={(event) => { this.editDraft = event.target.value; }}
                          onKeyDown={(event) => this.onEditKeyDown(event, note)}
                        />
                        <span className="jp-msg__edit-actions">
                          <button type="button" className="jp-msg__edit-cancel" onClick={this.cancelEdit} disabled={this.savingEdit}>Cancel</button>
                          <button type="button" className="jp-msg__edit-save" onClick={() => this.saveEdit(note)} disabled={!this.editDraft.trim() || this.savingEdit}>
                            {this.savingEdit ? 'Saving…' : 'Save'}
                          </button>
                        </span>
                      </div>
                    ) : (
                      <div
                        className="jp-msg__bubble"
                        title={continued ? moment(a.created_at).format('h:mm A') : undefined}
                        role={mine ? 'button' : undefined}
                        tabIndex={mine ? 0 : undefined}
                        aria-expanded={mine ? selected : undefined}
                        onClick={mine ? () => this.toggleSelected(note) : undefined}
                        onKeyDown={mine ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this.toggleSelected(note); } } : undefined}
                      >
                        {a.body}
                      </div>
                    )}
                    {!editing && (
                      <span className="jp-msg__meta">
                        <time dateTime={a.created_at} title={formatTime(a.created_at)}>{moment(a.created_at).format('h:mm A')}</time>
                      </span>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div className="jp-chat__composer">
          <textarea
            ref={this.inputRef}
            className="jp-chat__input"
            rows={1}
            placeholder="Write a note for the team…"
            aria-label="Write a note"
            value={this.draft}
            disabled={this.sending}
            onChange={(event) => { this.draft = event.target.value; }}
            onKeyDown={this.onComposerKeyDown}
          />
          <button
            type="button"
            className="jp-chat__send"
            onClick={this.sendNote}
            disabled={!this.draft.trim() || this.sending}
            aria-label="Send note"
            title="Send (Enter)"
          >
            {this.sending ? <Spinner size={14} /> : <Icon icon="send-message" iconSize={14} />}
          </button>
        </div>
        <p className="jp-chat__hint">Enter to send, Shift+Enter for a new line.</p>

        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete note"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <strong>Delete this note?</strong>
          <p>It’s removed from the job and can’t be brought back.</p>
        </Alert>
      </div>
    )
  }
}

export default NotesTable;